import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import recallService from '../../../services/recalls.service';
import Loading from '../../common/Loading';

const STATUS_OPTIONS = ['PENDING', 'IN_PROCESS', 'SENT', 'FAILED', 'COMPLETED'];
const MEDIUM_OPTIONS = ['EMAIL', 'SMS', 'CALL'];

function RecallPoolEdit() {
    const { poolId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        preferredName: '',
        email: '',
        cellNumber: '',
        phoneNumber: '',
        recallNumber: 1,
        mediumType: 'EMAIL',
        status: 'PENDING',
        firstRecallStatus: false,
        firstRecallDate: '',
        secondRecallStatus: false,
        secondRecallDate: '',
        thirdRecallStatus: false,
        thirdRecallDate: '',
        fourthRecallStatus: false,
        fourthRecallDate: '',
    });

    useEffect(() => {
        let active = true;
        setLoading(true);
        recallService
            .getRecallPoolById(poolId)
            .then((res) => {
                if (!active) return;
                const data = res.data || {};
                setForm({
                    firstName: data.firstName || '',
                    lastName: data.lastName || '',
                    preferredName: data.preferredName || '',
                    email: data.email || '',
                    cellNumber: data.cellNumber || '',
                    phoneNumber: data.phoneNumber || '',
                    recallNumber: data.recallNumber ?? 1,
                    mediumType: data.mediumType || 'EMAIL',
                    status: data.status || 'PENDING',
                    firstRecallStatus: Boolean(data.firstRecallStatus),
                    firstRecallDate: data.firstRecallDate || '',
                    secondRecallStatus: Boolean(data.secondRecallStatus),
                    secondRecallDate: data.secondRecallDate || '',
                    thirdRecallStatus: Boolean(data.thirdRecallStatus),
                    thirdRecallDate: data.thirdRecallDate || '',
                    fourthRecallStatus: Boolean(data.fourthRecallStatus),
                    fourthRecallDate: data.fourthRecallDate || '',
                });
            })
            .catch((err) => {
                console.error(err);
                if (active) setError('Failed to load recall pool entry.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [poolId]);

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await recallService.updateRecallPool(poolId, {
                ...form,
                recallNumber: Number(form.recallNumber),
                firstRecallDate: form.firstRecallDate || null,
                secondRecallDate: form.secondRecallDate || null,
                thirdRecallDate: form.thirdRecallDate || null,
                fourthRecallDate: form.fourthRecallDate || null,
            });
            toast.success('Recall pool updated');
            navigate('/recall-pools');
        } catch (err) {
            console.error(err);
            toast.error('Failed to update recall pool');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loading />;

    return (
        <div className="container-fluid">
            <div className="pagetitle mb-3">
                <h1>
                    <i className="bi bi-pencil-square"></i> Edit Recall Pool
                </h1>
                <Link to="/recall-pools" className="small">
                    ← Back to list
                </Link>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row className="g-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control
                                        value={form.firstName}
                                        onChange={(e) => updateField('firstName', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                        value={form.lastName}
                                        onChange={(e) => updateField('lastName', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Preferred Name</Form.Label>
                                    <Form.Control
                                        value={form.preferredName}
                                        onChange={(e) => updateField('preferredName', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => updateField('email', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Cell Number</Form.Label>
                                    <Form.Control
                                        value={form.cellNumber}
                                        onChange={(e) => updateField('cellNumber', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Phone Number</Form.Label>
                                    <Form.Control
                                        value={form.phoneNumber}
                                        onChange={(e) => updateField('phoneNumber', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Recall Number</Form.Label>
                                    <Form.Select
                                        value={form.recallNumber}
                                        onChange={(e) => updateField('recallNumber', e.target.value)}
                                    >
                                        {[1, 2, 3, 4].map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Medium</Form.Label>
                                    <Form.Select
                                        value={form.mediumType}
                                        onChange={(e) => updateField('mediumType', e.target.value)}
                                    >
                                        {MEDIUM_OPTIONS.map((m) => (
                                            <option key={m} value={m}>
                                                {m}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={form.status}
                                        onChange={(e) => updateField('status', e.target.value)}
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <hr className="my-4" />
                        <h5 className="mb-3">Recall Stages</h5>
                        <Row className="g-3">
                            {[
                                ['first', 'First'],
                                ['second', 'Second'],
                                ['third', 'Third'],
                                ['fourth', 'Fourth'],
                            ].map(([key, label]) => (
                                <Col md={6} key={key}>
                                    <Card className="bg-light border-0">
                                        <Card.Body>
                                            <Form.Check
                                                type="switch"
                                                id={`${key}-status`}
                                                label={`${label} recall sent`}
                                                checked={form[`${key}RecallStatus`]}
                                                onChange={(e) =>
                                                    updateField(`${key}RecallStatus`, e.target.checked)
                                                }
                                            />
                                            <Form.Group className="mt-2">
                                                <Form.Label>{label} recall date</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={form[`${key}RecallDate`]}
                                                    onChange={(e) =>
                                                        updateField(`${key}RecallDate`, e.target.value)
                                                    }
                                                />
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        <div className="d-flex gap-2 mt-4">
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? 'Saving…' : 'Save changes'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={() => navigate('/recall-pools')}
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
}

export default RecallPoolEdit;
